/**
 * Your own account: what you are called, your password, and signing out.
 *
 * WHY THE NAME MATTERS MORE THAN IT LOOKS. Callables identify the actor by uid,
 * because that is the only thing stable and unforgeable. Anything that then
 * PRINTS a person falls back through display name, then email, then the raw
 * uid - so an org where nobody has filled this in produces invitations reading
 * "EtIgKAUNHwNNETkgTYUsleeYmQC2 has given you access", which is what Damon
 * received and what prompted this panel. The same name appears on notes,
 * mentions and the team list.
 *
 * It is stored on the Firebase Auth record rather than in Firestore, because
 * that is the one place every other feature already reads a person from - the
 * team list, the invite mail and the mention email all resolve names through
 * the Auth record. A second copy in Firestore would be a second thing to keep
 * in step, and the two would disagree the first time one write failed.
 */

import { useState } from 'react';
import { updateProfile } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../firebase';

export function AccountPanel({ email, onSignOut }: { email: string | null; onSignOut: () => void }) {
  const user = auth.currentUser;
  const [name, setName] = useState(user?.displayName ?? '');
  const [saved, setSaved] = useState(user?.displayName ?? '');
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const dirty = name.trim() !== saved.trim();

  async function save() {
    const trimmed = name.trim();
    if (!auth.currentUser) return;
    setBusy('name');
    setMsg(null);
    try {
      await updateProfile(auth.currentUser, { displayName: trimmed });
      // Re-read so anything else on this page asking the SDK gets the new
      // value, rather than the stale one cached on the user object.
      await auth.currentUser.reload();
      setSaved(trimmed);
      setMsg({
        ok: true,
        text: trimmed
          ? 'Saved. This is the name people will see on invitations and notes.'
          : 'Name cleared. Invitations you send will show your email address instead.',
      });
    } catch (err) {
      setMsg({ ok: false, text: err instanceof Error ? err.message : 'Could not save that.' });
    } finally {
      setBusy(null);
    }
  }

  async function resetPassword() {
    if (!email) return;
    setBusy('reset');
    setMsg(null);
    try {
      await httpsCallable(functions, 'requestPasswordReset')({
  email, host: window.location.hostname,
});
      setMsg({
        ok: true,
        text: `A link to set a new password is on its way to ${email}. It expires within the hour.`,
      });
    } catch (err) {
      setMsg({ ok: false, text: err instanceof Error ? err.message : 'Could not send that.' });
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="panel">
      <h2 className="panel-title">Account</h2>

      <dl className="kv">
        <dt>Signed in as</dt>
        <dd className="mono">{email ?? '--'}</dd>
      </dl>

      <label className="acct-label" htmlFor="acct-name">Your name</label>
      <div className="acct-row">
        <input
          id="acct-name"
          className="acct-input"
          type="text"
          autoComplete="name"
          placeholder="Damon Dinsmore"
          value={name}
          maxLength={80}
          disabled={busy !== null}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && dirty) void save(); }}
        />
        <button className="btn" type="button" disabled={!dirty || busy !== null} onClick={() => void save()}>
          {busy === 'name' ? 'Saving...' : 'Save'}
        </button>
      </div>
      <p className="panel-note" style={{ marginTop: 6 }}>
        {saved.trim()
          ? 'Shown on invitations you send, on notes, and in the team list.'
          : 'Not set yet. Until you fill this in, invitations you send show your email address.'}
      </p>

      <div className="acct-actions">
        <button className="btn" type="button" disabled={!email || busy !== null} onClick={() => void resetPassword()}>
          {busy === 'reset' ? 'Sending...' : 'Send me a password reset'}
        </button>
        <button className="btn" type="button" onClick={onSignOut}>Sign out</button>
      </div>

      {msg && (
        <div className={'banner ' + (msg.ok ? 'banner-ok' : 'banner-error')} style={{ marginTop: 12 }}>
          {msg.text}
        </div>
      )}
    </section>
  );
}
